package com.flowyride.core.domain.loop

import com.flowyride.core.domain.model.GeoPoint
import com.flowyride.core.domain.model.Route
import com.flowyride.core.domain.model.RoutePreferences

/** Where the loop heads first. */
enum class CompassDirection(val bearingDegrees: Double) {
    N(0.0), NE(45.0), E(90.0), SE(135.0), S(180.0), SW(225.0), W(270.0), NW(315.0),
}

/** How long the rider wants to ride. */
sealed interface LoopTarget {
    data class Distance(val meters: Double) : LoopTarget {
        init {
            require(meters in LoopLimits.DISTANCE_METERS) { "Loop distance out of range: $meters m" }
        }
    }

    data class Duration(val seconds: Double) : LoopTarget {
        init {
            require(seconds in LoopLimits.DURATION_SECONDS) { "Loop duration out of range: $seconds s" }
        }
    }
}

object LoopLimits {
    val DISTANCE_METERS = 20_000.0..600_000.0
    val DURATION_SECONDS = 1_800.0..36_000.0
}

data class LoopRequest(
    val start: GeoPoint,
    val target: LoopTarget,
    /** Null lets the generator pick one. */
    val direction: CompassDirection?,
    val preferences: RoutePreferences = RoutePreferences(),
    /** Same seed, same loop: the rider asks for another one by changing it. */
    val seed: Int = 0,
)

/**
 * @param waypoints Departure, generated stops, then the departure again.
 * @param route The ride through [waypoints], already computed.
 */
data class GeneratedLoop(
    val waypoints: List<GeoPoint>,
    val route: Route,
)

/** Builds a round trip from a single departure. */
interface LoopGenerator {
    /** @throws com.flowyride.core.domain.routing.RoutingException */
    suspend fun generate(request: LoopRequest): GeneratedLoop
}
